<template>
  <div>
    <div v-if="isMobileScreen && !showForm">
      <button
        class="btn-hide w-full text-center block mt-4"
        @click="hideButton"
      >
        {{
          this.showForm == true
            ? $t('Hide Form')
            : $t(current_board.form_button_text)
        }}
      </button>
    </div>
    <transition name="slideDown">
      <div v-if="showForm">
        <form
          enctype="multipart/form-data"
          @submit.prevent="fetchForm()"
          accept-charset="UTF-8"
          method="post"
          class="mt-4"
          id="new-idea-form"
        >
          <slot name="honey"></slot>
          <div>
            <AuthorSelect
              v-if="currentProjectAdmin"
              :label="$t('Author Name')"
              :options="allUsers"
              :current-user="currentUser"
              :show-options="showOptions"
              :board-post-author-select="boardPostAuthorSelect"
              v-model="postAuthor"
              @showAuthorEmailField="showAuthorEmailField"
              @hideAuthorEmailField="hideAuthorEmailField"
              @addSelectedAuthor="addSelectedAuthor"
              @removeSelectedAuthor="removeSelectedAuthor"
              @showDropdown="showDropdown"
            />
          </div>
          <section v-if="authorEmailField" class="new-author-section">
            <div class="new-author-fields">
              <div class="new-author-field">
                <label class="new-author-label">{{ $t("Author name") }}</label>
                <input
                  id="author_name_display"
                  :value="postAuthor"
                  type="text"
                  class="new-author-input"
                  disabled
                />
              </div>
              <div class="new-author-field">
                <label class="new-author-label">{{ $t("Author email") }}</label>
                <input
                  id="author_email"
                  :placeholder="$t('email@example.com')"
                  v-model="authorEmail"
                  type="email"
                  name="post[email]"
                  class="new-author-input"
                  autocomplete="off"
                />
              </div>
            </div>

            <label
              class="new-author-checkbox"
              v-if="!projectPrivate"
            >
              <input
                type="checkbox"
                value="1"
                name="post[notify_user]"
                v-model="agreeCheckbox"
              />
              <span>{{ $t("Notify the user about updates and comments to this idea") }}</span>
            </label>
          </section>
          <input name="utf8" type="hidden" value="✓" />
          <input
            type="hidden"
            name="authenticity_token"
            :value="this.form_token"
          />

          <div
            v-if="this.draft_info_shown"
            class="draft-message mt-3 text-sm text-dblue-500 flex items-center justify-between gap-2"
          >
            <span>{{ $t('We saved an unpublished draft for you.') }}</span>
            <a
              href="#"
              @click.prevent="deleteDraftAndResetFields()"
              class="font-medium shrink-0"
            >{{ $t("Delete") }}</a>
          </div>

          <!-- Spam honeypot: visually offscreen, and hidden from assistive tech
               so screen readers / autofill never fill it (a filled value
               silently rejects the submission server-side). -->
          <div
            class="mt-3"
            v-if="!user_signed_in && this.projectSsoOnly !== 'true'"
            aria-hidden="true"
          >
            <label class="email">{{ $t('repeat your email address (required)') }}</label>
            <input
              type="email"
              class="email"
              name="remark"
              tabindex="-1"
              autocomplete="off"
              pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
              placeholder="name@domain.com"
            />
          </div>

          <div class="mt-3" id="post-title">
            <label class="label">{{ $t(current_board.form_title) }}</label>
            <input
              id="post_title"
              :placeholder="$t(current_board.form_title_placeholder)"
              v-model="new_post_title"
              autocomplete="off"
              required="required"
              type="text"
              name="post[title]"
              class="input"
            />
          </div>
          <div class="mt-3" id="post-content">
            <label class="label">{{ $t(current_board.form_content) }}</label>
            <RichTextEditor
              ref="postEditor"
              v-model="new_post_content"
              input-id="post_content"
              input-name="post[content]"
              variant="minimal"
              :placeholder="$t(current_board.form_content_placeholder)"
              :enable-image-upload="true"
              :enable-embeds="false"
              :enable-drafts="true"
              :draft-id="`new_post_${current_board.id}`"
              upload-url="/posts/images"
            />
          </div>
          <div v-if="error" class="text-red-700 pt-4">
            <p>{{ error }}</p>
          </div>
          <div ref="guestChallenge" class="pt-2"></div>
          <div v-if="emailError" class="text-red-700 pt-4 cursor-pointer">
            <p>
              {{ $t('Email is already in use.') }}
              <a @click="clickLoginBtn()"> {{ $t('Click here') }} </a>
              {{ $t('to login and add your idea.') }}
            </p>
          </div>
          <FileUpload
            name="post[attachments][]"
            class="mt-3"
            :noun="$t('images')"
            upy-id="post-image"
          >
          </FileUpload>
          <div v-if="!user_signed_in && this.projectSsoOnly !== 'true'">
            <div
              v-if="anonymousPostAuthorLocked"
              class="mt-3 px-3 py-2 bg-gray-100 rounded-md text-sm text-neutral-800"
            >
              <div class="flex items-start justify-between gap-2">
                <div>
                  <div class="text-xs text-neutral-600">{{ $t("Posting as") }}</div>
                  <div class="font-medium text-neutral-900">{{ name }}</div>
                  <div class="text-neutral-700">{{ email }}</div>
                </div>
                <a
                  href="#"
                  @click.prevent="unlockAnonymousAuthor"
                  class="text-xs text-neutral-700 underline hover:no-underline shrink-0"
                >{{ $t("Not you?") }}</a>
              </div>
              <input type="hidden" name="post[name]" :value="name" />
              <input type="hidden" name="post[email]" :value="email" />
            </div>
            <template v-else>
              <div
                class="mt-3 px-2 py-2 bg-gray-50 rounded-t-md"
                id="post-name"
              >
                <label class="label"
                  >{{ $t("Name") }} {{ allow_anonymous_posts ? "" : "*" }}</label
                >
                <input
                  id="name"
                  :placeholder="$t('Your name')"
                  v-model="name"
                  autocomplete="off"
                  :required="!allow_anonymous_posts"
                  type="text"
                  name="post[name]"
                  class="input"
                />
              </div>
              <div
                class="pt-3 px-2 py-2 bg-gray-50"
                id="post-email"
              >
                <label class="label"
                  >{{ $t("Email") }} {{ allow_anonymous_posts ? "" : "*" }}</label
                >
                <input
                  id="email"
                  :placeholder="$t('Your email address')"
                  v-model="email"
                  autocomplete="off"
                  :required="!allow_anonymous_posts"
                  type="email"
                  name="post[email]"
                  class="input"
                />
              </div>
            </template>
          </div>
          <p
            class="whitespace-pre-line pt-2 text-neutral-800 text-left break-words px-2 py-2 bg-gray-50"
            v-if="formFooterText"
          >
            {{ formFooterText }}
          </p>
          <div
            class="pt-3 text-neutral-700 text-xs text-width flex items-start gap-2 px-2 py-2 bg-gray-50"
            :class="{ 'rounded-b-md': !allow_anonymous_posts }"
            v-if="!user_signed_in && !anonymousPostAuthorLocked"
            id="post-agree"
          >
            <input name="user[agree_checkbox]" type="hidden" value="0" />
            <input
              :required="!allow_anonymous_posts"
              type="checkbox"
              value="1"
              name="user[agree_checkbox]"
              id="user_agree_checkbox"
              v-model="agree_checkbox"
              class="mt-0.5 h-4 w-4 shrink-0"
            />
            <div>
              <span>{{ $t("I agree with") }}</span>
              <a
                href="https://www.feedbear.com/privacy-policy"
                :title="$t('Privacy Policy')"
                target="_blank"
                class="text-neutral-600 underline hover:no-underline hover:text-project-500"
                >{{ $t("Privacy Policy") }}</a
              >
              {{ $t("and") }}
              <a
                href="https://www.feedbear.com/terms"
                :title="$t('Terms of Service')"
                target="_blank"
                class="text-neutral-600 underline hover:no-underline hover:text-project-500"
                >{{ $t("Terms and Conditions") }}</a
              >
              {{ allow_anonymous_posts ? "" : "*" }}
            </div>
          </div>
          <div
            class="pt-3 text-neutral-700 text-xs px-2 py-2 bg-gray-50 rounded-b-md"
            v-if="allow_anonymous_posts && !user_signed_in && !anonymousPostAuthorLocked"
          >
            {{ $t('Optional. If you provide your email address, we will notify you when your feedback receives comments or updates.') }}
          </div>
          <input
            :value="this.timeNow"
            type="hidden"
            name="post[agreed_with_privacy_policy]"
            id="user_agreed_with_privacy_policy"
          />

          <input
            v-model="user_ip_address"
            type="hidden"
            name="post[user_ip_address]"
          />

          <div
            v-if="emailChallenge"
            class="mt-3 px-3 py-3 bg-gray-50 rounded-md"
            id="post-email-code"
          >
            <label for="email_code" class="label font-semibold">{{ $t('Check your email') }}</label>
            <p class="text-sm text-neutral-700 mt-1 mb-2">
              {{ $t('Enter the 6-digit code we sent to {email} to post your idea.', { email: emailCodeAddress }) }}
            </p>
            <input
              id="email_code"
              ref="emailCode"
              v-model="emailCode"
              :placeholder="$t('6-digit code')"
              autocomplete="one-time-code"
              inputmode="numeric"
              maxlength="6"
              required="required"
              type="text"
              class="input"
            />
            <p v-if="emailCodeError" role="alert" class="text-sm text-red-700 mt-2">{{ emailCodeError }}</p>
            <a
              href="#"
              @click.prevent="resendEmailCode"
              class="mt-2 inline-block text-xs text-neutral-700 underline hover:no-underline"
            >{{ $t('Send a new code') }}</a>
          </div>

          <div class="mt-3" id="post-submit-btn">
            <button
              class="btn-main w-full text-center block"
              @click.prevent="ssoPopUp()"
              v-if="
                !user_signed_in &&
                this.projectSsoOnly === 'true' &&
                customWebsiteHref != ''
              "
            >
              {{ $t(current_board.form_button_text) }}
            </button>
            <button
              name="button"
              type="submit"
              class="btn-main w-full text-center block"
              @click="deleteDraft()"
              :disabled="isSubmitting"
              v-else
            >
              <div class="flex items-center justify-center gap-2">
                <svg v-if="isSubmitting" class="spinner" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="20px" width="20px" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 6l0 -3"></path>
                  <path d="M16.25 7.75l2.15 -2.15"></path>
                  <path d="M18 12l3 0"></path>
                  <path d="M16.25 16.25l2.15 2.15"></path>
                  <path d="M12 18l0 3"></path>
                  <path d="M7.75 16.25l-2.15 2.15"></path>
                  <path d="M6 12l-3 0"></path>
                  <path d="M7.75 7.75l-2.15 -2.15"></path>
                </svg>
                {{ isSubmitting ? '' : $t(current_board.form_button_text) }}
              </div>
            </button>
          </div>
        </form>
        <Dialog v-model="ssoLoginPopup" :portal="true">
          <div class="px-4 pt-2 pb-4 sm:px-6">
            <div class="text-center font-semibold">{{ $t('Log in to proceed') }}</div>
            <div class="p-4 text-center">
              {{ $t('You must be logged in to submit feedback') }}, <br />{{ $t('click the button below to login') }}
            </div>
            <div class="flex justify-center">
              <a
                :href="this.customWebsiteHref"
                @click="ssoAddPost()"
                class="text-white rounded-lg shadow-sm px-4 py-2 text-sm font-medium"
                style="background-color: var(--accent-color)"
              >
                {{ $t('Sign up / Log in') }}
              </a>
            </div>
          </div>
        </Dialog>
      </div>
    </transition>
  </div>
</template>

<script>
import EventBus from "event_bus";
import { guestCaptchaToken, renderGuestChallenge } from "../guest_captcha";
import Dialog from "../components/dialog";
import PaperclipIcon from "../images/icons/paperclip.svg";
import FileUpload from "../components/file_upload";
import { RichTextEditor } from "../components/rich_text_editor";
import AuthorSelect from "../roadmap/custom_select";
import debounce from "lodash/debounce";
import NewIdeaModal from "../roadmap/new_idea_modal.vue";
import { get } from "http";
import { isLoggedOutOnMobile } from "normalize-range";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "NewPost",
  props: [
    "token",
    "action",
    "board",
    "signed_in",
    "search_url",
    "time-now",
    "currentProjectUsersUrl",
    "form-footer-text",
    "project-sso-only",
    "custom-website-href",
    "current-user",
    "current-project-admin",
    "project-private",
    "board-post-author-select",
    "post-status",
    "boards-names",
    "allow-anonymous-posts",
    "is-admin",
    "anonymous-post-author-name",
    "anonymous-post-author-email",
  ],
  data: function () {
    return {
      form_action: this.action,
      form_token: this.token,
      current_board: this.board,
      user_signed_in: this.signed_in,
      new_post_title: "",
      new_post_content: "",
      name: this.anonymousPostAuthorName || "",
      email: this.anonymousPostAuthorEmail || "",
      // Mutable mirror of the server-remembered identity. Seeded from the prop
      // and flipped on after an anonymous submit so the locked "posting as"
      // block appears without needing a full page reload.
      anonymousAuthorRemembered: !!this.anonymousPostAuthorEmail,
      emailError: "",
      draft_info_shown: false,
      PaperclipIcon,
      user_ip_address: "",
      ssoLoginPopup: false,
      agree_checkbox: false,
      error: "",
      postAuthorName: "",
      selectedAuthor: this.currentUser ? this.currentUser.id : "",
      authorEmailField: false,
      agreeCheckbox: false,
      authorEmail: "",
      showOptions: true,
      showForm: false,
      isMobileScreen: false,
      isLoggedOutOnMobile: !this.isAdmin && window.innerWidth <= 480,
      allow_anonymous_posts: !!JSON.parse(this.allowAnonymousPosts),
      isSubmitting: false,
      guestChallengeToken: "",
      emailChallenge: "",
      emailCode: "",
      emailCodeAddress: "",
      emailCodeError: "",
    };
  },
  components: {
    FileUpload,
    RichTextEditor,
    Dialog,
    AuthorSelect,
    NewIdeaModal,
  },
  computed: {
    ...mapGetters(["allUsers"]),
    postAuthor: {
      get() {
        return this.postAuthorName;
      },
      set(val) {
        this.postAuthorName = val;
      },
    },
    displayModalBoardForm() {
      return this.isLoggedOutOnMobile;
    },
    anonymousPostAuthorLocked() {
      return !this.user_signed_in && this.anonymousAuthorRemembered;
    },
  },

  async mounted() {
    if (this.user_signed_in && this.currentProjectAdmin) {
      await this.$store.dispatch(
        "getcurrentProjectUsers",
        this.currentProjectUsersUrl
      );
    }

    if (localStorage.new_post_title) {
      this.new_post_title = localStorage.new_post_title;
      this.draft_info_shown = true;
    }
    if (localStorage.new_post_content) {
      this.new_post_content = localStorage.new_post_content;
      this.draft_info_shown = true;
    }
    var postData = JSON.parse(window.localStorage.getItem("post"));
    if (postData && postData.current_board_id == this.current_board.id) {
      this.new_post_title = postData.title;
      this.new_post_content = postData.content;
      this.agree_checkbox = postData.agree_checkbox;
      localStorage.removeItem("post");
    }
    this.isMobileScreen = window.innerWidth <= 480 ? true : false;
    this.showForm = !this.isMobileScreen;
  },
  watch: {
    // whenever user types a space, we should load suggestions
    new_post_title: function (term) {
      if (this.new_post_title.length < 4) {
        EventBus.$emit("hideSuggestions");
        localStorage.title = term;
      } else {
        this.debouncedGetSuggestions();
        localStorage.title = term;
      }
    },
    new_post_content: function (term) {
      localStorage.description = term;
    },
    // A code is bound to the address it was sent to.
    email: function () {
      this.emailChallenge = "";
      this.emailCode = "";
      this.emailCodeError = "";
    },
  },
  created() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (!this.anonymousPostAuthorLocked) {
      this.name = urlParams.get("name") || this.name;
      this.email = urlParams.get("email") || this.email;
    }
    this.showValues();
    this.debouncedGetSuggestions = debounce(this.getSuggestions, 500);
  },
  methods: {
    ...mapActions(["getcurrentProjectUsers"]),
    showValues: function () {
      if (localStorage.title) {
        this.new_post_title = localStorage.title;
      }
      if (localStorage.description) {
        this.new_post_content = localStorage.description;
      }
    },
    hideButton: function () {
      this.showForm = !this.showForm;
    },
    openRegModal: function () {
      EventBus.$emit("openRegModal");
      this.persistDraft();
    },
    persistDraft: function () {
      localStorage.new_post_title = this.new_post_title;
      localStorage.new_post_content = this.new_post_content;
    },
    ssoPopUp() {
      this.ssoLoginPopup = true;
    },
    showAuthorEmailField(postAuthor) {
      this.authorEmailField = true;
    },
    showDropdown(value) {
      this.showOptions = value;
    },
    hideAuthorEmailField() {
      this.authorEmailField = false;
      this.postAuthor = "";
    },

    addSelectedAuthor(authorId) {
      this.selectedAuthor = authorId;
      this.postAuthor = "";
    },

    removeSelectedAuthor() {
      this.selectedAuthor = null;
    },
    ssoAddPost() {
      const post_data = {
        title: this.new_post_title,
        content: this.new_post_content,
        agree_checkbox: this.agree_checkbox,
        current_board_id: this.current_board.id,
      };
      window.localStorage.setItem("post", JSON.stringify(post_data));
      this.ssoLoginPopup = false;
    },
    deleteDraftAndResetFields() {
      this.deleteDraft();
      this.clearRichTextDraft();
      this.resetFields();
    },
    clickLoginBtn: function () {
      // Keep the typed idea around and carry the email they already entered
      // into the auth dialog so they don't have to retype it.
      this.persistDraft();
      EventBus.$emit("prefillAuthEmail", this.email);
      const login = document.getElementById("auth-login-btn");
      if (login) {
        login.click();
      } else {
        // Fallback if the login button isn't found
        EventBus.$emit("openRegModal");
      }
    },
    unlockAnonymousAuthor: function () {
      // Re-open the name/email fields prefilled with the remembered identity;
      // the next submit overwrites the server-side remembered author.
      this.anonymousAuthorRemembered = false;
    },
    async fetchForm() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      const formElem = document.getElementById("new-idea-form");
      const formData = new FormData(formElem);
      if (!this.user_signed_in) {
        formData.append("captcha_token", await guestCaptchaToken("GUEST_POST"));
        if (this.guestChallengeToken) {
          formData.append("captcha_challenge_token", this.guestChallengeToken);
          this.guestChallengeToken = "";
        }
      }
      if (this.emailChallenge && this.emailCode) {
        formData.append("email_challenge", this.emailChallenge);
        formData.append("email_code", this.emailCode.trim());
      }
      localStorage.setItem("title", this.new_post_title);
      localStorage.setItem("description", this.new_post_content);

      if (this.user_signed_in && this.currentProjectAdmin) {
        if (this.selectedAuthor) {
          formData.append("post[author]", this.selectedAuthor);
        } else {
          formData.append("post[name]", this.postAuthor);
          if (!this.agreeCheckbox) formData.append("dont_notify_author", true);
        }
      }
      Rails.ajax({
        url: this.form_action,
        type: "POST",
        data: formData,
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          if (this.$refs.guestChallenge) this.$refs.guestChallenge.innerHTML = "";
          this.emailChallenge = "";
          this.emailCode = "";
          this.emailCodeError = "";
          this.clearSubmittedDrafts();
          this.resetFields();
          this.isSubmitting = false;
          if (data && data.notice) {
            // Moderated/hidden posts stay on the board — show the toast inline
            // and mirror what a fresh page load would have done: lock the now
            // server-remembered identity. resetFields() above already cleared
            // the title/body/drafts.
            this.showSubmitNotice(data.notice);
            if (!this.user_signed_in && this.name && this.email) {
              this.anonymousAuthorRemembered = true;
            }
          } else if (data && data.redirect_url) {
            window.location.href = data.redirect_url;
          }
        },
        error: (data, status) => {
          this.isSubmitting = false;
          if (data && data.challenge_required) {
            this.error = data.error;
            renderGuestChallenge(this.$refs.guestChallenge, "GUEST_POST").then((token) => {
              this.guestChallengeToken = token;
              this.fetchForm();
            });
          } else if (data && data.code === "email_code_required") {
            this.error = "";
            this.emailCodeError = "";
            this.emailChallenge = data.email_challenge;
            this.emailCodeAddress = data.email;
            this.emailCode = "";
            this.$nextTick(() => this.$refs.emailCode && this.$refs.emailCode.focus());
          } else if (data && data.code === "email_code_invalid") {
            this.emailCodeError = this.$t('That code is not right. Check it and try again, or send a new code.');
          } else if (data && data.email_used) {
            this.emailError = data.email_used;
          } else if (data && data.error_message) {
            this.error = data.error_message;
          } else {
            this.error = this.$t('An error occurred while submitting your idea. Please try again.');
          }
        },
      });
    },
    resendEmailCode() {
      this.emailChallenge = "";
      this.emailCode = "";
      this.emailCodeError = "";
      this.fetchForm();
    },
    deleteDraft() {
      localStorage.removeItem("new_post_title");
      localStorage.removeItem("new_post_content");
      this.draft_info_shown = false;
    },
    clearRichTextDraft() {
      EventBus.$emit("clearRichTextDraft", {
        inputId: "post_content",
        draftId: `new_post_${this.current_board.id}`
      });
    },
    clearSubmittedDrafts() {
      this.deleteDraft();
      localStorage.removeItem("title");
      localStorage.removeItem("description");
      this.clearRichTextDraft();
    },
    showSubmitNotice(notice) {
      if (!notice) return;
      this.$notify({
        group: "success",
        text: notice,
      });
    },

    resetFields() {
      this.new_post_title = "";
      this.new_post_content = "";
      // Clear the editor directly. Relying on the v-model watcher is unreliable:
      // a no-op Trix change can leave its isInternalChange guard set, which then
      // swallows the external "" update. clearContent() calls loadHTML('') itself.
      if (this.$refs.postEditor) {
        this.$refs.postEditor.clearContent();
      }
    },

    getSuggestions: debounce(function () {
      Rails.ajax({
        url: this.search_url + "?search_term=" + encodeURIComponent(this.new_post_title),
        type: "GET",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          if (data.length > 0) {
            EventBus.$emit("showSuggestions", data);
          }
        },
        error: (data, status) => {
          console.log(data);
        },
      });
    }, 500),
  },
};
</script>

<style scoped lang="scss">
.new-author-section {
  margin-top: 12px;
  padding: 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}

.new-author-fields {
  display: flex;
  gap: 12px;
}

.new-author-field {
  flex: 1;
}

.new-author-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #404040;
  margin-bottom: 6px;
}

.new-author-input {
  width: 100%;
  padding: 10px 12px;
  font-size: 13px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  outline: none;
  transition: all 0.15s ease;

  &::placeholder {
    color: #a3a3a3;
  }

  &:focus {
    border-color: var(--accent-color, #009be5);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-color, #009be5) 15%, transparent);
  }

  &:disabled {
    background: #f3f4f6;
    color: #737373;
    cursor: not-allowed;
  }
}

.new-author-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  font-size: 12px;
  color: #737373;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    margin-top: 1px;
    border-radius: 4px;
    cursor: pointer;
    accent-color: var(--accent-color, #009be5);
  }

  span {
    line-height: 1.4;
  }
}

@media screen and (max-width: 480px) {
  .new-author-fields {
    flex-direction: column;
  }
}

.draft-message {
  position: relative;
  padding: 0.5rem 0.75rem;
  z-index: 1;
}

.draft-message::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--accent-color);
  opacity: 0.08;
  pointer-events: none;
  z-index: 0;

  @apply rounded-md;
}
.btn-SignSso {
  width: 160px;
  color: #fff;
}
/* Honeypot. Must stay a logical property: a physical left: -9999px gives
   right-to-left boards 10,000px of sideways scroll. */
.email {
  position: absolute;
  inset-inline-start: -9999px;
}

@media only screen and (max-width: 480px) {
  .slideDown-enter,
  .slideDown-leave-to {
    opacity: 0;
    transform: translateY(-0.5rem);
  }

  .slideDown-enter-to,
  .slideDown-leave {
    opacity: 1;
    transform: translateY(0);
  }

  .slideDown-enter-active,
  .slideDown-leave-active {
    @apply transition duration-150 ease-in;
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
